import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { vendorApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
  AreaChart, Area, Legend,
} from 'recharts';

const v = (n) => n ?? 0;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const STATUS_CARDS = (s) => [
  {
    label: 'Pending',
    sublabel: 'Awaiting your response',
    value: v(s?.assignedRequests),
    from: '#f59e0b', to: '#f97316',
    filter: 'ASSIGNED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'In Process',
    sublabel: 'Submitted for review',
    value: v(s?.vendorSubmitted),
    from: '#3b82f6', to: '#818cf8',
    filter: 'VENDOR_SUBMITTED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'Approved',
    sublabel: 'OmniDrive approved · Awaiting client',
    value: v(s?.companyApproved),
    from: '#10b981', to: '#06b6d4',
    filter: 'COMPANY_APPROVED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Awaiting CVA',
    sublabel: 'Pending CVA final approval',
    value: v(s?.pendingFinalApproval),
    from: '#d97706', to: '#f59e0b',
    filter: 'PENDING_FINAL_APPROVAL',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Fully Approved',
    sublabel: 'CVA approved · Fully done',
    value: v(s?.clientApproved),
    from: '#8b5cf6', to: '#ec4899',
    filter: 'CLIENT_APPROVED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Rejected',
    sublabel: 'Needs revision',
    value: v(s?.rejected),
    from: '#ef4444', to: '#f97316',
    filter: 'REJECTED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Cancelled',
    sublabel: 'Process permanently cancelled',
    value: v(s?.cancelled),
    from: '#6b7280', to: '#9ca3af',
    filter: 'CANCELLED',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    ),
  },
];

export default function VendorDashboard() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery('vendor-stats', vendorApi.getDashboardStats, { refetchInterval: 60000 });
  const stats = data?.data;

  // Fetch all assignments to build real monthly trend data
  const { data: asgnData } = useQuery(
    ['vendor-assignments-all'],
    () => vendorApi.getAssignments({ size: 1000 }),
    { refetchInterval: 60000 }
  );
  const allAssignments = useMemo(() => {
    const raw = asgnData?.data;
    return raw?.content ?? raw ?? [];
  }, [asgnData]);

  const barData = useMemo(() => [
    { name: 'Pending',         value: v(stats?.assignedRequests),    fill: '#f59e0b' },
    { name: 'Submitted',       value: v(stats?.vendorSubmitted),      fill: '#3b82f6' },
    { name: 'OD Approved',     value: v(stats?.companyApproved),      fill: '#6366f1' },
    { name: 'Awaiting CVA',    value: v(stats?.pendingFinalApproval), fill: '#d97706' },
    { name: 'Fully Approved',  value: v(stats?.clientApproved),       fill: '#10b981' },
    { name: 'Rejected',        value: v(stats?.rejected),      fill: '#ef4444' },
    { name: 'Cancelled',       value: v(stats?.cancelled),            fill: '#9ca3af' },
  ], [stats]);

  // Real 6-month activity trend derived from fetched assignments
  const lineData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const monthDate = subMonths(now, 5 - i);
      const from = startOfMonth(monthDate).getTime();
      const to   = endOfMonth(monthDate).getTime();
      const inMonth = (dateStr) => {
        if (!dateStr) return false;
        const t = new Date(dateStr).getTime();
        return t >= from && t <= to;
      };
      const month = format(monthDate, 'MMM');
      const monthAssignments = allAssignments.filter(a => inMonth(a.assignedAt));
      return {
        month,
        Pending:   monthAssignments.filter(a => a.status === 'ASSIGNED').length,
        Submitted: monthAssignments.filter(a => a.status === 'VENDOR_SUBMITTED').length,
        Approved:  monthAssignments.filter(a => ['COMPANY_APPROVED','CLIENT_APPROVED','PENDING_FINAL_APPROVAL'].includes(a.status)).length,
      };
    });
  }, [allAssignments]);

  if (isLoading) return <LoadingSpinner message="Loading your dashboard..." />;

  const pending = v(stats?.assignedRequests);

  return (
    <div className="space-y-6 pb-8">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Vendor Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">Your personal assignment overview</p>
        </div>
        {pending > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            {pending} pending
          </span>
        )}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-7 gap-4">
        {STATUS_CARDS(stats).map((card) => (
          <button
            key={card.label}
            onClick={() => navigate(`/vendor/assignments?status=${card.filter}`)}
            className="relative overflow-hidden rounded-2xl p-5 text-left group transition-transform hover:-translate-y-0.5 hover:shadow-lg"
            style={{ background: `linear-gradient(135deg, ${card.from}, ${card.to})` }}
          >
            <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white opacity-10" />
            <div className="absolute -right-1 -bottom-6 h-24 w-24 rounded-full bg-white opacity-10" />
            <div className="relative z-10 pr-12">
              <div>
                <p className="text-white text-opacity-80 text-xs font-medium uppercase tracking-wider">{card.label}</p>
                <p className="text-white text-4xl font-black mt-1 leading-none">{card.value}</p>
                <p className="text-white text-opacity-70 text-xs mt-1.5">{card.sublabel}</p>
              </div>
            </div>
            <div className="absolute z-10 top-5 right-5 flex h-10 w-10 items-center justify-center rounded-xl bg-white bg-opacity-20 group-hover:bg-opacity-30 transition-colors">
              {card.icon}
            </div>
            <p className="relative text-white text-opacity-70 text-xs mt-3 font-medium">View →</p>
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Bar chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Assignment Breakdown</h2>
              <p className="text-xs text-gray-400 mt-0.5">Current status counts</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} barSize={32} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {barData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Area chart — real monthly trend */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Activity Trend</h2>
              <p className="text-xs text-gray-400 mt-0.5">6-month assignment history</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={lineData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <defs>
                {[['gP','#f59e0b'],['gS','#3b82f6'],['gA','#10b981']].map(([id,c])=>(
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="10%" stopColor={c} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={c} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Area type="monotone" dataKey="Pending"   stroke="#f59e0b" strokeWidth={2} fill="url(#gP)" dot={false} />
              <Area type="monotone" dataKey="Submitted" stroke="#3b82f6" strokeWidth={2} fill="url(#gS)" dot={false} />
              <Area type="monotone" dataKey="Approved"  stroke="#10b981" strokeWidth={2} fill="url(#gA)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CTA banner */}
      {pending > 0 && (
        <div
          className="rounded-2xl p-5 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg,#1e40af,#4f46e5)' }}
        >
          <div>
            <p className="text-white font-bold">You have {pending} assignment{pending > 1 ? 's' : ''} waiting</p>
            <p className="text-blue-200 text-sm mt-0.5">Submit your part articles to move them forward</p>
          </div>
          <button
            onClick={() => navigate('/vendor/assignments?status=ASSIGNED')}
            className="bg-white text-blue-700 px-5 py-2 rounded-xl text-sm font-bold hover:bg-blue-50 transition-colors flex-shrink-0"
          >
            Open Assignments →
          </button>
        </div>
      )}
    </div>
  );
}
